/** A font the picker lists: one of the web-safe fonts every system has, or a Google font. */
export interface FontEntry {
  family: string;
  category: string;
  /** Google's variants, such as `400,400i,700`, which its stylesheet URL names. */
  variants: string;
  /** Google's subsets, the languages the font covers; empty for a local font. */
  subsets: string[];
  local: boolean;
}

/** The category pills, in the order the old plugin showed them. A font without a category is `other`. */
export const FONT_CATEGORIES = ['serif', 'sans-serif', 'display', 'handwriting', 'monospace', 'other'];

/** Google's subsets, as the language select offers them. */
export const FONT_LANGUAGES: ReadonlyArray<{value: string; label: string}> = [
  {value: 'arabic', label: 'Arabic'},
  {value: 'bengali', label: 'Bengali'},
  {value: 'chinese-hongkong', label: 'Chinese (Hong Kong)'},
  {value: 'chinese-simplified', label: 'Chinese (Simplified)'},
  {value: 'chinese-traditional', label: 'Chinese (Traditional)'},
  {value: 'cyrillic', label: 'Cyrillic'},
  {value: 'cyrillic-ext', label: 'Cyrillic Extended'},
  {value: 'devanagari', label: 'Devanagari'},
  {value: 'greek', label: 'Greek'},
  {value: 'greek-ext', label: 'Greek Extended'},
  {value: 'gujarati', label: 'Gujarati'},
  {value: 'gurmukhi', label: 'Gurmukhi'},
  {value: 'hebrew', label: 'Hebrew'},
  {value: 'japanese', label: 'Japanese'},
  {value: 'kannada', label: 'Kannada'},
  {value: 'khmer', label: 'Khmer'},
  {value: 'korean', label: 'Korean'},
  {value: 'latin', label: 'Latin'},
  {value: 'latin-ext', label: 'Latin Extended'},
  {value: 'malayalam', label: 'Malayalam'},
  {value: 'myanmar', label: 'Myanmar'},
  {value: 'oriya', label: 'Oriya'},
  {value: 'sinhala', label: 'Sinhala'},
  {value: 'tamil', label: 'Tamil'},
  {value: 'telugu', label: 'Telugu'},
  {value: 'thai', label: 'Thai'},
  {value: 'tibetan', label: 'Tibetan'},
  {value: 'vietnamese', label: 'Vietnamese'},
];

const GENERIC_FAMILY: Record<string, string> = {
  serif: 'serif',
  'sans-serif': 'sans-serif',
  monospace: 'monospace',
  handwriting: 'cursive',
};

/** A font as `fonts.json` keeps it, keyed by family: the old plugin's own shape. */
interface StoredFont {
  category?: string;
  variants?: string;
  subsets?: string;
  local?: boolean;
}

let catalogue: Promise<FontEntry[]> | null = null;

/**
 * The 1,000 fonts of the old jQuery plugin (its 7 local ones first, then 993 Google fonts from 2020), from a JSON
 * chunk fetched the first time a font field needs it. The page designer's Froala font list is the same Google list.
 * A failed fetch is not kept: the next call fetches again.
 */
export const loadFontCatalogue = (): Promise<FontEntry[]> => {
  catalogue ??= import('./fonts.json').then(({default: fonts}) => Object.entries(fonts as Record<string, StoredFont>)
    .map(([family, font]) => ({
      family,
      category: font.category || 'other',
      variants: font.variants || '',
      subsets: font.subsets ? font.subsets.split(',') : [],
      local: !!font.local,
    })), (error: unknown) => {
    catalogue = null;
    throw error;
  });
  return catalogue;
};

/** A `font-family` value showing the font, falling back to its category's generic family until it has loaded. */
export const fontStack = (family: string, category = ''): string => `'${family}', ${GENERIC_FAMILY[category] ?? 'sans-serif'}`;

const requested = new WeakMap<Document, Set<string>>();

/**
 * Adds Google's stylesheet for a font to the page, once per page, with the URL the old plugin used. A local font
 * needs none: it is installed.
 */
export const loadFontFace = (font: FontEntry, doc: Document): void => {
  if (font.local) {
    return;
  }
  const families = requested.get(doc) ?? new Set<string>();
  requested.set(doc, families);
  if (families.has(font.family)) {
    return;
  }
  families.add(font.family);
  const link = doc.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css?family=${font.family.replace(/ /g, '+')}:${font.variants}&display=swap`;
  doc.head.appendChild(link);
};
